import { Tooltip, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useMicrobiologyResultDataContext } from "Pages/MicroBiologyLis/ResultData/ResultDataContext";
import CustomPagination from "Shared/JsxPagination";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ColumnSetup from "../../../Shared/ColumnSetup/ColumnSetup";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import { emptyObjectValues } from "../../../Utils/Common/Requisition";
import { AutocompleteStyle } from "../../../Utils/MuiStyles/AutocompleteStyles";
import TransferTestsModal from "./Modal/TransferTestsModal";
import ViewContactsModal from "./Modal/ViewContactsModal";
import ViewHistoricalDataModal from "./Modal/ViewHistoricalDataModal";
import ReqDataGrid from "./ResultDataGrid";
import {
  getActiveTab,
  getSortedGridHeaders,
  getVisibleTabHeaders,
} from "./rowDataUtils";

const TabSelected = styled(Tab)(AutocompleteStyle());

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ p: 0 }}>
        <Typography>{children}</Typography>
      </Box>
    )}
  </div>
);

const getA11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const MicrobiologyResultData: React.FC = () => {
  const {
    data,
    filterData,
    setFilterData,
    searchValue,
    setSearchValue,
    selectedBox,
    setSelectedBox,
    loadGridData,
    apiCalls,
    loading,
    setCheckedAll,
    total,
    value,
    setValue,
    setRowsToExpand,
    setIsMasterExpandTriggered,
  } = useMicrobiologyResultDataContext();

  const { t } = useLang();
  const labTimeZone = useSelector(
    (state: any) => state?.Reducer?.selectedTenantInfo?.labTimeZone
  );
  const [filters, setFilters] = useState<any[]>([]);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [isInitialRender2, setIsInitialRender2] = useState(false);
  const [triggerSearchData, setTriggerSearchData] = useState(false);
  const [showViewContacts, setShowViewContacts] = useState(false);
  const [showViewHistoricalData, setShowViewHistoricalData] = useState(false);
  const [historicalDataReqOrderId, setHistoricalDataReqOrderId] = useState<number | undefined>();
  const [showTransferTests, setShowTransferTests] = useState(false);

  const {
    curPage,
    nextPage,
    pageNumbers,
    pageSize,
    prevPage,
    setCurPage,
    setPageSize,
    setTotal,
    showPage,
    totalPages,
  } = usePagination();

  const clearSelectedBox = () => {
    setSelectedBox((prevState: any) => ({
      ...prevState,
      requisitionId: [],
    }));
    setCheckedAll(false);
  };

  const resetFilterData = () => {
    filterData.filters = [];
    filterData.sortColumn = "";
    filterData.pageSize = 50;
    filterData.pageNumber = 1;
    filterData.sortDirection = "";
  };

  useEffect(() => {
    setTotal(total);
  }, [total, setTotal]);

  useEffect(() => {
    setRowsToExpand([]);
    setIsMasterExpandTriggered(false);
  }, [curPage, pageSize, setRowsToExpand, setIsMasterExpandTriggered]);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
      pageNumber: curPage,
      pageSize: pageSize,
    }));
  }, [curPage, pageSize, setFilterData]);

  useEffect(() => {
    if (!isInitialRender2) {
      setIsInitialRender2(true);
      return;
    }
    loadGridData(true);
  }, [pageSize, curPage, triggerSearchData]);

  useEffect(() => {
    const filteredObject: Record<string, any> = {};
    filters?.forEach((filter: any) => {
      filteredObject[filter.label] = filter.filterValue;
    });

    const uniqueKeys = new Set<string>();
    Object.entries(filteredObject).forEach(([key, value]) => {
      if (value) {
        uniqueKeys.add(key);
      }
    });
    setSearchedTags(Array.from(uniqueKeys));
  }, [filters]);

  // Reset only when the last tag is removed (count goes from >0 to 0). On mount the
  // initial load comes from apiCalls() below, so this must not fetch as well.
  const prevTagsLength = useRef(searchedTags.length);
  useEffect(() => {
    const hadTags = prevTagsLength.current > 0;
    prevTagsLength.current = searchedTags.length;
    if (hadTags && searchedTags.length === 0) {
      resetSearch();
    }
  }, [searchedTags.length]);

  useEffect(() => {
    apiCalls();
  }, []);

  const sortedTabs = getSortedGridHeaders(data.gridHeaders);
  const activeTab = getActiveTab(data.gridHeaders, value);
  const activeTabHeaders = activeTab?.tabHeaders || [];

  const handleChange = async (_: React.SyntheticEvent, newValue: number) => {
    const selectedTab = sortedTabs[newValue];
    const apiTabId = selectedTab?.tabID;

    setValue(newValue);
    if (apiTabId && filterData?.tabId !== apiTabId) {
      const emptySearchObj = emptyObjectValues(searchValue);
      setSearchValue(emptySearchObj);
      filterData.tabId = apiTabId;
      setCurPage(1);
      setRowsToExpand([]);
      filterData.filters = [];
      filterData.pageNumber = 1;
      setFilters([]);
      await loadGridData();
    }
    clearSelectedBox();
  };

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearchData((prev) => !prev);
  };

  const resetSearch = () => {
    clearSelectedBox();
    resetFilterData();
    setFilters([]);
    const emptySearchObj = emptyObjectValues(searchValue);
    setSearchValue(emptySearchObj);
    loadGridData(true);
  };

  const handleTagRemoval = (clickedTag: string) => {
    const filteredTags = filters.filter((tab: any) => tab.label !== clickedTag);
    const filteredDataTags = filterData.filters.filter((tab: any) => tab.label !== clickedTag);

    setFilters(filteredTags);
    setSearchValue((prevValue: any) => ({
      ...prevValue,
      filters: filteredTags,
    }));
    setFilterData((prevValue: any) => ({
      ...prevValue,
      filters: filteredDataTags,
    }));
  };

  return (
    <>
      <ColumnSetup
        show={showSetupModal}
        closeSetupModal={() => setShowSetupModal(false)}
        loadData={apiCalls}
        value={filterData.tabId}
        columnsToUse={activeTabHeaders}
      />

      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-toolbar py-3 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <Tooltip title={t("Setup")} arrow placement="top">
                <button
                  className="btn btn-icon btn-sm fw-bold btn-setting btn-icon-light"
                  onClick={() => setShowSetupModal(true)}
                >
                  <i className="fa fa-gear"></i>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column flex-column-fluid">
          <div className="app-content flex-column-fluid">
            <div className="app-container container-fluid">
              <Tabs
                value={value}
                onChange={handleChange}
                TabIndicatorProps={{ style: { background: "transparent" } }}
                className="min-h-auto"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-scrollButtons": {
                    width: 0,
                    transition: "width 0.7s ease",
                    "&:not(.Mui-disabled)": {
                      width: "48px",
                    },
                  },
                }}
              >
                {sortedTabs.map((items: any, index: number) => (
                    <TabSelected
                      data-test-id={items?.tabName?.replace(/\s/g, "")}
                      key={items.tabID}
                      label={t(items.tabName)}
                      {...getA11yProps(index)}
                      className="fw-bold text-capitalize"
                      disabled={loading}
                    />
                  ))}
              </Tabs>
              <div className="card tab-content-card">
                <div className="card-body py-2">
                  <div className="d-flex gap-4 flex-wrap mb-1">
                    {searchedTags.map((tag) => (
                      <div
                        className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                        onClick={() => handleTagRemoval(tag)}
                        key={tag}
                      >
                        <span className="fw-bold">{tag}</span>
                        <i className="bi bi-x"></i>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                    <div className="d-flex gap-2 responsive-flexed-actions">
                      <div className="d-flex align-items-center">
                        <span className="fw-400 mr-3">{t("Records")}</span>
                        <select
                          id={`MicrobiologyResultDataRecords`}
                          className="form-select w-125px h-33px rounded py-2"
                          onChange={async (e) => {
                            const newPageSize = parseInt(e.target.value);
                            setPageSize(newPageSize);
                            filterData.pageSize = newPageSize;
                            await loadGridData();
                          }}
                          value={filterData.pageSize}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="150">150</option>
                          <option value="200">200</option>
                        </select>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                      <button
                        id={`MicrobiologyResultDataSearch`}
                        onClick={handleSearch}
                        className="btn btn-linkedin btn-sm fw-500"
                        aria-controls="Search"
                      >
                        {t("Search")}
                      </button>
                      <button
                        onClick={resetSearch}
                        type="button"
                        className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                        id={`MicrobiologyResultDataReset`}
                      >
                        <span>
                          <span>{t("Reset")}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                  <TabPanel value={value} index={value}>
                    <ReqDataGrid
                      tabsInfo={activeTabHeaders}
                      labTimeZone={labTimeZone}
                      value={value}
                      filters={filters}
                      setFilters={setFilters}
                      setCurPage={setCurPage}
                    />
                  </TabPanel>
                  {!loading && getVisibleTabHeaders(activeTabHeaders).length > 0 && (
                    <CustomPagination
                      curPage={curPage}
                      nextPage={nextPage}
                      pageNumbers={pageNumbers}
                      pageSize={pageSize}
                      prevPage={prevPage}
                      showPage={showPage}
                      total={total}
                      totalPages={totalPages}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <ViewContactsModal
          show={showViewContacts}
          onHide={() => setShowViewContacts(false)}
        />

        <ViewHistoricalDataModal
          show={showViewHistoricalData}
          onHide={() => {
            setShowViewHistoricalData(false);
            setHistoricalDataReqOrderId(undefined);
          }}
          reqOrderId={historicalDataReqOrderId}
          tabId={filterData?.tabId}
        />

        <TransferTestsModal
          show={showTransferTests}
          onHide={() => setShowTransferTests(false)}
        />
      </div>
    </>
  );
};

export default React.memo(MicrobiologyResultData);
